import { forwardRef } from "react";

/* -----------------------------------------------------------
   DashboardUI — Composants partagés du dashboard admin
   Thème "Aventure & Temps" · Lisibilité opérationnelle avant tout
   ----------------------------------------------------------- */

/* --- Titres de page --- */
export function PageHeader({ chapter, title, subtitle, actions, testId }) {
  return (
    <div
      className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 pb-6 border-b border-or/15"
      data-testid={testId}
    >
      <div>
        {chapter && (
          <span className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-or/80">
            {chapter}
          </span>
        )}
        <h1 className="mt-2 font-display font-medium text-3xl md:text-4xl text-parchemin leading-tight tracking-tight">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-1.5 text-sm text-parchemin/60">{subtitle}</p>
        )}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

/* --- Card conteneur générique --- */
export function Card({ children, className = "", testId }) {
  return (
    <section
      className={`bg-noir-50/50 border border-or/15 rounded-sm p-5 md:p-6 ${className}`}
      data-testid={testId}
    >
      {children}
    </section>
  );
}

/* --- KPI card unifié : palette or/vert/parchemin --- */
export function Kpi({ icon, label, value, accent = "or", testId }) {
  const accents = {
    or: "text-or border-or/30 bg-gradient-to-br from-or/10 to-transparent",
    vert: "text-vert-50 border-vert-50/30 bg-gradient-to-br from-vert-50/10 to-transparent",
    parchemin:
      "text-parchemin border-parchemin/25 bg-gradient-to-br from-parchemin/5 to-transparent",
    oxyde:
      "text-oxyde border-oxyde/40 bg-gradient-to-br from-oxyde/10 to-transparent",
  };
  return (
    <div
      data-testid={testId}
      className={`border rounded-sm p-5 flex flex-col ${accents[accent] || accents.or}`}
    >
      {icon && <div className="text-2xl mb-2 leading-none">{icon}</div>}
      <div className="font-display font-medium text-2xl md:text-3xl tracking-tight stat-graved text-parchemin">
        {value}
      </div>
      <div className="mt-1 font-mono text-[10px] tracking-[0.2em] uppercase text-parchemin/60">
        {label}
      </div>
    </div>
  );
}

/* --- Statut badge --- */
export function Badge({ tone = "neutral", children, testId }) {
  const tones = {
    success: "bg-vert-50/15 text-vert-50 border-vert-50/40",
    warning: "bg-or/15 text-or border-or/40",
    danger: "bg-oxyde/15 text-oxyde border-oxyde/50",
    info: "bg-or-100/10 text-or-100 border-or-100/40",
    neutral: "bg-parchemin/5 text-parchemin/70 border-parchemin/20",
  };
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-wider border ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

/* --- Boutons cohérents --- */
export function Btn({
  variant = "primary",
  size = "md",
  children,
  className = "",
  ...props
}) {
  const variants = {
    primary:
      "bg-or hover:bg-or-100 text-noir font-medium shadow-[0_8px_24px_-10px_rgba(200,146,42,0.5)]",
    secondary:
      "border border-or/30 hover:border-or hover:bg-or/10 text-parchemin",
    ghost: "text-parchemin/70 hover:text-or hover:bg-or/5",
    danger:
      "border border-oxyde/40 hover:border-oxyde hover:bg-oxyde/10 text-oxyde",
    dangerText: "text-oxyde hover:bg-oxyde/10",
  };
  const sizes = {
    sm: "px-2.5 py-1 text-xs tracking-wide",
    md: "px-4 py-2 text-sm tracking-wide",
    lg: "px-6 py-2.5 text-sm tracking-wide",
  };
  return (
    <button
      {...props}
      className={`inline-flex items-center justify-center gap-1.5 rounded-sm transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed ${variants[variant]} ${sizes[size]} ${className}`}
    >
      {children}
    </button>
  );
}

/* --- Formulaires --- */
export function FieldLabel({ children }) {
  return (
    <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-or/70">
      {children}
    </label>
  );
}

const inputBase =
  "px-3 py-2.5 rounded-sm bg-noir-100 border border-or/20 text-parchemin text-sm outline-none focus:border-or focus:ring-1 focus:ring-or/30 transition placeholder:text-parchemin/30";

export const Input = forwardRef(function Input(props, ref) {
  return <input ref={ref} className={`${inputBase} ${props.className || ""}`} {...props} />;
});

export const Select = forwardRef(function Select(props, ref) {
  return (
    <select
      ref={ref}
      className={`${inputBase} pr-8 ${props.className || ""}`}
      {...props}
    />
  );
});

export const Textarea = forwardRef(function Textarea(props, ref) {
  return (
    <textarea
      ref={ref}
      className={`${inputBase} ${props.className || ""}`}
      {...props}
    />
  );
});

export function Field({
  label,
  value,
  onChange,
  type = "text",
  required,
  step,
  min,
  max,
  testid,
  placeholder,
}) {
  return (
    <div className="flex flex-col gap-2">
      <FieldLabel>{label}</FieldLabel>
      <Input
        data-testid={testid}
        type={type}
        step={step}
        min={min}
        max={max}
        value={value}
        required={required}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

/* --- Wrapper tableau (lisibilité opérationnelle) --- */
export function DataTable({ children, testId }) {
  return (
    <div className="overflow-x-auto border border-or/15 rounded-sm bg-noir-50/40">
      <table className="w-full text-sm" data-testid={testId}>
        {children}
      </table>
    </div>
  );
}

export function TH({ align = "left", children, className = "" }) {
  return (
    <th
      className={`p-3 font-mono text-[10px] tracking-[0.2em] uppercase text-or/70 text-${align} bg-noir-100/60 ${className}`}
    >
      {children}
    </th>
  );
}

export function TD({ align = "left", children, className = "" }) {
  return (
    <td className={`p-3 text-parchemin/85 text-${align} ${className}`}>
      {children}
    </td>
  );
}

/* --- État vide élégant --- */
export function EmptyState({ icon, title, hint, testId }) {
  return (
    <div
      className="flex flex-col items-center justify-center py-16 text-parchemin/50 border border-or/10 border-dashed rounded-sm bg-noir-50/30"
      data-testid={testId}
    >
      {icon && <div className="mb-3 text-or/50">{icon}</div>}
      <p className="text-sm italic font-display">{title}</p>
      {hint && <p className="mt-1 text-xs text-parchemin/40">{hint}</p>}
    </div>
  );
}
